import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Calendar, Clock, Plus, Trash2, Edit, CheckCircle, AlertTriangle, Bell, ChevronLeft, ChevronRight, Loader2, Settings, Flag, Link, Copy } from 'lucide-react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const FRONTEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const TIPOS_EVENTO = [
  { value: 'reunion', label: 'Reunión', icon: '👥' },
  { value: 'deadline', label: 'Deadline', icon: '⏰' },
  { value: 'recordatorio', label: 'Recordatorio', icon: '🔔' },
  { value: 'evento_institucional', label: 'Evento Institucional', icon: '🏛️' },
  { value: 'tarea', label: 'Tarea', icon: '📋' },
  { value: 'otro', label: 'Otro', icon: '📌' }
];

const PRIORIDADES = [
  { value: 'baja', label: 'Baja', color: 'bg-green-100 text-green-800', dot: 'bg-green-500' },
  { value: 'media', label: 'Media', color: 'bg-blue-100 text-blue-800', dot: 'bg-blue-500' },
  { value: 'alta', label: 'Alta', color: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500' },
  { value: 'urgente', label: 'Urgente', color: 'bg-red-100 text-red-800', dot: 'bg-red-500' }
];

const ESTADOS = [
  { value: 'pendiente', label: 'Pendiente', color: 'bg-gray-100 text-gray-800' },
  { value: 'en_proceso', label: 'En proceso', color: 'bg-blue-100 text-blue-800' },
  { value: 'completado', label: 'Completado', color: 'bg-green-100 text-green-800' },
  { value: 'cancelado', label: 'Cancelado', color: 'bg-red-100 text-red-800' }
];

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

export default function AdminCalendarioInternoPage() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [eventos, setEventos] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [config, setConfig] = useState(null);
  
  // Vista del calendario
  const [mesActual, setMesActual] = useState(new Date());
  const [vistaActiva, setVistaActiva] = useState('resumen'); // 'resumen' | 'calendario' | 'lista'
  
  // Modal de evento
  const [showEventoModal, setShowEventoModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [eventoEdicion, setEventoEdicion] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Formulario de evento
  const [formEvento, setFormEvento] = useState({
    titulo: '',
    descripcion: '',
    tipo: 'otro',
    prioridad: 'media',
    fecha_inicio: '',
    hora_inicio: '',
    fecha_fin: '',
    hora_fin: '',
    todo_el_dia: false,
    responsables: [],
    notificar_al_crear: true,
    notas: ''
  });
  
  // Nuevo responsable
  const [nuevoResponsable, setNuevoResponsable] = useState({ email: '', nombre: '', notificar: true });
  
  // Config temp
  const [configTemp, setConfigTemp] = useState({ emails_cd: [], notificaciones_activas: true });
  const [nuevoEmailCD, setNuevoEmailCD] = useState('');

  useEffect(() => {
    fetchData();
  }, [token, mesActual]);

  const fetchData = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const primerDia = new Date(mesActual.getFullYear(), mesActual.getMonth(), 1);
      const ultimoDia = new Date(mesActual.getFullYear(), mesActual.getMonth() + 1, 0);
      const fechaDesde = primerDia.toISOString().split('T')[0];
      const fechaHasta = ultimoDia.toISOString().split('T')[0];
      
      const [eventosRes, resumenRes, configRes] = await Promise.all([
        axios.get(`${API_URL}/calendario-interno/admin/eventos?fecha_desde=${fechaDesde}&fecha_hasta=${fechaHasta}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/calendario-interno/admin/resumen`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/calendario-interno/admin/configuracion`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setEventos(eventosRes.data.eventos);
      setResumen(resumenRes.data);
      setConfig(configRes.data);
      setConfigTemp(configRes.data);
    } catch (err) {
      console.error('Error cargando datos:', err);
      toast.error('Error al cargar el calendario');
    } finally {
      setLoading(false);
    }
  };

  const handleCrearEvento = async () => {
    if (!formEvento.titulo || !formEvento.fecha_inicio) {
      toast.error('Completá título y fecha');
      return;
    }
    
    setSubmitting(true);
    try {
      if (eventoEdicion) {
        await axios.put(`${API_URL}/calendario-interno/admin/eventos/${eventoEdicion.id}`, formEvento, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Evento actualizado');
      } else {
        await axios.post(`${API_URL}/calendario-interno/admin/eventos`, formEvento, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Evento creado');
      }
      
      cerrarModal();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEliminarEvento = async (eventoId) => {
    if (!window.confirm('¿Eliminar este evento?')) return;
    
    try {
      await axios.delete(`${API_URL}/calendario-interno/admin/eventos/${eventoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Evento eliminado');
      fetchData();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const handleCompletarEvento = async (eventoId) => {
    try {
      await axios.post(`${API_URL}/calendario-interno/admin/eventos/${eventoId}/completar`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Evento completado');
      fetchData();
    } catch (err) {
      toast.error('Error al completar');
    }
  };

  const handleGuardarConfig = async () => {
    try {
      await axios.put(`${API_URL}/calendario-interno/admin/configuracion`, configTemp, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Configuración guardada');
      setShowConfigModal(false);
      setConfig(configTemp);
    } catch (err) {
      toast.error('Error al guardar');
    }
  };

  const abrirModalNuevo = () => {
    setEventoEdicion(null);
    setFormEvento({
      titulo: '',
      descripcion: '',
      tipo: 'otro',
      prioridad: 'media',
      fecha_inicio: new Date().toISOString().split('T')[0],
      hora_inicio: '',
      fecha_fin: '',
      hora_fin: '',
      todo_el_dia: false,
      responsables: [],
      notificar_al_crear: true,
      notas: ''
    });
    setShowEventoModal(true);
  };

  const abrirModalEdicion = (evento) => {
    setEventoEdicion(evento);
    setFormEvento({
      titulo: evento.titulo,
      descripcion: evento.descripcion || '',
      tipo: evento.tipo,
      prioridad: evento.prioridad,
      fecha_inicio: evento.fecha_inicio,
      hora_inicio: evento.hora_inicio || '',
      fecha_fin: evento.fecha_fin || '',
      hora_fin: evento.hora_fin || '',
      todo_el_dia: evento.todo_el_dia || false,
      responsables: evento.responsables || [],
      notificar_al_crear: false,
      notas: evento.notas || ''
    });
    setShowEventoModal(true);
  };

  const cerrarModal = () => {
    setShowEventoModal(false);
    setEventoEdicion(null);
  };

  const agregarResponsable = () => {
    if (!nuevoResponsable.email) return;
    
    setFormEvento({
      ...formEvento,
      responsables: [...formEvento.responsables, { ...nuevoResponsable }]
    });
    setNuevoResponsable({ email: '', nombre: '', notificar: true });
  };

  const quitarResponsable = (index) => {
    setFormEvento({
      ...formEvento,
      responsables: formEvento.responsables.filter((_, i) => i !== index)
    });
  };

  const agregarEmailCD = () => {
    if (!nuevoEmailCD || configTemp.emails_cd.includes(nuevoEmailCD)) return;
    setConfigTemp({ ...configTemp, emails_cd: [...configTemp.emails_cd, nuevoEmailCD] });
    setNuevoEmailCD('');
  };

  const quitarEmailCD = (email) => {
    setConfigTemp({ ...configTemp, emails_cd: configTemp.emails_cd.filter(e => e !== email) });
  };

  const formatearFecha = (fecha) => {
    try {
      const [year, month, day] = fecha.split('-');
      return `${day}/${month}/${year}`;
    } catch {
      return fecha;
    }
  };

  const getPrioridadBadge = (prioridad) => {
    const p = PRIORIDADES.find(pr => pr.value === prioridad);
    return <Badge className={p?.color}>{p?.label}</Badge>;
  };

  const getTipoBadge = (tipo) => {
    const t = TIPOS_EVENTO.find(te => te.value === tipo);
    return <span>{t?.icon} {t?.label}</span>;
  };

  const getEstadoBadge = (estado) => {
    const e = ESTADOS.find(es => es.value === estado);
    return <Badge className={e?.color}>{e?.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Calendario Interno CD</h1>
          <p className="text-gray-600">Gestión de eventos y recordatorios de la Comisión Directiva</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowConfigModal(true)}>
            <Settings className="w-4 h-4 mr-2" />
            Configuración
          </Button>
          <Button onClick={abrirModalNuevo} data-testid="btn-nuevo-evento">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Evento
          </Button>
        </div>
      </div>

      {/* Tabs de vista */}
      <div className="flex gap-2 border-b pb-2">
        {['resumen', 'calendario', 'lista'].map(v => (
          <Button
            key={v}
            variant={vistaActiva === v ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setVistaActiva(v)}
          >
            {v.charAt(0).toUpperCase() + v.slice(1)}
          </Button>
        ))}
      </div>

      {/* Vista Resumen */}
      {vistaActiva === 'resumen' && resumen && (
        <div className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="py-4 text-center">
                <Calendar className="w-6 h-6 mx-auto mb-2 text-blue-500" />
                <div className="text-2xl font-bold">{resumen.stats.hoy}</div>
                <div className="text-sm text-gray-500">Hoy</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4 text-center">
                <Bell className="w-6 h-6 mx-auto mb-2 text-purple-500" />
                <div className="text-2xl font-bold">{eventos.filter(e => {
                  if (!e.fecha_inicio) return false;
                  const [y, m] = e.fecha_inicio.split('-').map(Number);
                  return y === mesActual.getFullYear() && m === mesActual.getMonth() + 1;
                }).length}</div>
                <div className="text-sm text-gray-500">Este mes</div>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="py-4 text-center">
                <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-red-500" />
                <div className="text-2xl font-bold text-red-600">{resumen.stats.urgentes_pendientes}</div>
                <div className="text-sm text-red-600">Urgentes</div>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="py-4 text-center">
                <Clock className="w-6 h-6 mx-auto mb-2 text-amber-500" />
                <div className="text-2xl font-bold text-amber-600">{resumen.stats.deadlines_proximos}</div>
                <div className="text-sm text-amber-600">Deadlines</div>
              </CardContent>
            </Card>
          </div>

          {/* Eventos de hoy */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Hoy
              </CardTitle>
            </CardHeader>
            <CardContent>
              {resumen.eventos_hoy.length > 0 ? (
                <div className="space-y-3">
                  {resumen.eventos_hoy.map(evento => (
                    <EventoCard 
                      key={evento.id} 
                      evento={evento}
                      onEdit={() => abrirModalEdicion(evento)}
                      onComplete={() => handleCompletarEvento(evento.id)}
                      onDelete={() => handleEliminarEvento(evento.id)}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No hay eventos para hoy</p>
              )}
            </CardContent>
          </Card>

          {/* Urgentes */}
          {resumen.urgentes.length > 0 && (
            <Card className="border-red-200">
              <CardHeader className="bg-red-50">
                <CardTitle className="flex items-center gap-2 text-red-700">
                  <AlertTriangle className="w-5 h-5" />
                  Urgentes Pendientes
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-3">
                  {resumen.urgentes.map(evento => (
                    <EventoCard 
                      key={evento.id} 
                      evento={evento}
                      onEdit={() => abrirModalEdicion(evento)}
                      onComplete={() => handleCompletarEvento(evento.id)}
                      onDelete={() => handleEliminarEvento(evento.id)}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Eventos del mes */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="sm" onClick={() => setMesActual(new Date(mesActual.getFullYear(), mesActual.getMonth() - 1))}>
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <CardTitle>Eventos de {MESES[mesActual.getMonth()]} {mesActual.getFullYear()}</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setMesActual(new Date(mesActual.getFullYear(), mesActual.getMonth() + 1))}>
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {(() => {
                // Filtrar eventos del mes actual
                const year = mesActual.getFullYear();
                const month = mesActual.getMonth();
                const eventosDelMes = eventos.filter(e => {
                  if (!e.fecha_inicio) return false;
                  const [y, m] = e.fecha_inicio.split('-').map(Number);
                  return y === year && m === month + 1;
                }).sort((a, b) => a.fecha_inicio.localeCompare(b.fecha_inicio));
                
                return eventosDelMes.length > 0 ? (
                  <div className="space-y-3">
                    {eventosDelMes.map(evento => (
                      <EventoCard 
                        key={evento.id} 
                        evento={evento}
                        showDate
                        onEdit={() => abrirModalEdicion(evento)}
                        onComplete={() => handleCompletarEvento(evento.id)}
                        onDelete={() => handleEliminarEvento(evento.id)}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No hay eventos en este mes</p>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Vista Lista */}
      {vistaActiva === 'lista' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <Button variant="ghost" onClick={() => setMesActual(new Date(mesActual.getFullYear(), mesActual.getMonth() - 1))}>
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <CardTitle>{MESES[mesActual.getMonth()]} {mesActual.getFullYear()}</CardTitle>
              <Button variant="ghost" onClick={() => setMesActual(new Date(mesActual.getFullYear(), mesActual.getMonth() + 1))}>
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {eventos.length > 0 ? (
              <div className="space-y-3">
                {eventos.map(evento => (
                  <EventoCard 
                    key={evento.id} 
                    evento={evento}
                    showDate
                    onEdit={() => abrirModalEdicion(evento)}
                    onComplete={() => handleCompletarEvento(evento.id)}
                    onDelete={() => handleEliminarEvento(evento.id)}
                  />
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No hay eventos en este mes</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Vista Calendario - Placeholder simple */}
      {vistaActiva === 'calendario' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <Button variant="ghost" onClick={() => setMesActual(new Date(mesActual.getFullYear(), mesActual.getMonth() - 1))}>
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <CardTitle>{MESES[mesActual.getMonth()]} {mesActual.getFullYear()}</CardTitle>
              <Button variant="ghost" onClick={() => setMesActual(new Date(mesActual.getFullYear(), mesActual.getMonth() + 1))}>
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {(() => {
              // Calcular días del mes
              const year = mesActual.getFullYear();
              const month = mesActual.getMonth();
              const firstDay = new Date(year, month, 1);
              const lastDay = new Date(year, month + 1, 0);
              const daysInMonth = lastDay.getDate();
              const startDayOfWeek = firstDay.getDay(); // 0 = domingo
              
              // Crear array de días
              const days = [];
              // Días vacíos al inicio
              for (let i = 0; i < startDayOfWeek; i++) {
                days.push(null);
              }
              // Días del mes
              for (let d = 1; d <= daysInMonth; d++) {
                days.push(d);
              }
              
              // Obtener eventos para cada día (incluyendo eventos de varios días)
              const getEventosDelDia = (dia) => {
                if (!dia) return [];
                const fechaStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
                return eventos.filter(e => {
                  // Evento empieza este día
                  if (e.fecha_inicio === fechaStr) return true;
                  // Evento de varios días que incluye este día
                  if (e.fecha_fin && e.fecha_inicio && e.fecha_fin > e.fecha_inicio) {
                    return fechaStr >= e.fecha_inicio && fechaStr <= e.fecha_fin;
                  }
                  return false;
                });
              };
              
              const hoy = new Date();
              const esHoy = (dia) => {
                return dia && 
                  hoy.getDate() === dia && 
                  hoy.getMonth() === month && 
                  hoy.getFullYear() === year;
              };
              
              const getTipoIcon = (tipo) => {
                const t = TIPOS_EVENTO.find(x => x.value === tipo);
                return t ? t.icon : '📌';
              };
              
              const getPrioridadDot = (prioridad) => {
                const p = PRIORIDADES.find(x => x.value === prioridad);
                return p ? p.dot : 'bg-gray-500';
              };

              return (
                <div>
                  {/* Header días de la semana */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(d => (
                      <div key={d} className="text-center text-sm font-medium text-gray-500 py-2">
                        {d}
                      </div>
                    ))}
                  </div>
                  
                  {/* Grilla de días */}
                  <div className="grid grid-cols-7 gap-1">
                    {days.map((dia, idx) => {
                      const eventosDelDia = getEventosDelDia(dia);
                      return (
                        <div 
                          key={idx}
                          className={`min-h-[100px] border rounded-lg p-1 ${
                            dia ? 'bg-white hover:bg-gray-50' : 'bg-gray-50'
                          } ${esHoy(dia) ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}
                        >
                          {dia && (
                            <>
                              <div className={`text-sm font-medium mb-1 ${esHoy(dia) ? 'text-blue-600' : 'text-gray-700'}`}>
                                {dia}
                              </div>
                              <div className="space-y-1">
                                {eventosDelDia.slice(0, 3).map(evento => (
                                  <div
                                    key={evento.id}
                                    onClick={() => abrirModalEdicion(evento)}
                                    className="text-xs p-1 rounded bg-gray-100 hover:bg-gray-200 cursor-pointer truncate flex items-center gap-1"
                                    title={evento.titulo}
                                  >
                                    <span className={`w-1.5 h-1.5 rounded-full ${getPrioridadDot(evento.prioridad)}`}></span>
                                    <span>{getTipoIcon(evento.tipo)}</span>
                                    <span className="truncate">{evento.titulo}</span>
                                  </div>
                                ))}
                                {eventosDelDia.length > 3 && (
                                  <div className="text-xs text-gray-500 text-center">
                                    +{eventosDelDia.length - 3} más
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Modal Evento */}
      <Dialog open={showEventoModal} onOpenChange={setShowEventoModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{eventoEdicion ? 'Editar Evento' : 'Nuevo Evento'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Título *</Label>
              <Input
                value={formEvento.titulo}
                onChange={(e) => setFormEvento({ ...formEvento, titulo: e.target.value })}
                placeholder="Ej: Reunión de CD, Deadline presentación..."
                data-testid="input-titulo-evento"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo</Label>
                <Select value={formEvento.tipo} onValueChange={(v) => setFormEvento({ ...formEvento, tipo: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_EVENTO.map(t => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.icon} {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Prioridad</Label>
                <Select value={formEvento.prioridad} onValueChange={(v) => setFormEvento({ ...formEvento, prioridad: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORIDADES.map(p => (
                      <SelectItem key={p.value} value={p.value}>
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${p.dot}`} />
                          {p.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Descripción</Label>
              <Textarea
                value={formEvento.descripcion}
                onChange={(e) => setFormEvento({ ...formEvento, descripcion: e.target.value })}
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fecha inicio *</Label>
                <Input
                  type="date"
                  value={formEvento.fecha_inicio}
                  onChange={(e) => setFormEvento({ ...formEvento, fecha_inicio: e.target.value })}
                />
              </div>
              <div>
                <Label>Hora inicio</Label>
                <Input
                  type="time"
                  value={formEvento.hora_inicio}
                  onChange={(e) => setFormEvento({ ...formEvento, hora_inicio: e.target.value })}
                  disabled={formEvento.todo_el_dia}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fecha fin (opcional)</Label>
                <Input
                  type="date"
                  value={formEvento.fecha_fin}
                  onChange={(e) => setFormEvento({ ...formEvento, fecha_fin: e.target.value })}
                  min={formEvento.fecha_inicio}
                />
              </div>
              <div>
                <Label>Hora fin</Label>
                <Input
                  type="time"
                  value={formEvento.hora_fin}
                  onChange={(e) => setFormEvento({ ...formEvento, hora_fin: e.target.value })}
                  disabled={formEvento.todo_el_dia}
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                checked={formEvento.todo_el_dia}
                onCheckedChange={(checked) => setFormEvento({ ...formEvento, todo_el_dia: checked })}
              />
              <Label>Todo el día</Label>
            </div>
            
            <div>
              <Label>Notas internas</Label>
              <Textarea
                value={formEvento.notas}
                onChange={(e) => setFormEvento({ ...formEvento, notas: e.target.value })}
                placeholder="Notas adicionales..."
                rows={2}
              />
            </div>
            
            {/* Responsables */}
            <div>
              <Label>Responsables (recibirán notificaciones)</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  placeholder="Email"
                  value={nuevoResponsable.email}
                  onChange={(e) => setNuevoResponsable({ ...nuevoResponsable, email: e.target.value })}
                  className="flex-1"
                />
                <Input
                  placeholder="Nombre"
                  value={nuevoResponsable.nombre}
                  onChange={(e) => setNuevoResponsable({ ...nuevoResponsable, nombre: e.target.value })}
                  className="w-32"
                />
                <Button type="button" variant="outline" onClick={agregarResponsable}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {formEvento.responsables.length > 0 && (
                <div className="mt-2 space-y-1">
                  {formEvento.responsables.map((r, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                      <span>{r.nombre || r.email}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 text-red-500"
                        onClick={() => quitarResponsable(idx)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {!eventoEdicion && (
              <div className="flex items-center gap-2 p-3 bg-blue-50 rounded">
                <Switch
                  checked={formEvento.notificar_al_crear}
                  onCheckedChange={(checked) => setFormEvento({ ...formEvento, notificar_al_crear: checked })}
                />
                <Label>Notificar a responsables al crear</Label>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={cerrarModal}>Cancelar</Button>
            <Button onClick={handleCrearEvento} disabled={submitting}>
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {eventoEdicion ? 'Guardar cambios' : 'Crear evento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Config */}
      <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configuración del Calendario</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Link público */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Label className="flex items-center gap-2 text-blue-800">
                <Link className="w-4 h-4" />
                Link público (solo lectura)
              </Label>
              <p className="text-xs text-blue-600 mb-2">Compartí este link con la CD para que vean el calendario sin loguearse</p>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={`${FRONTEND_URL}/calendario-cd/${configTemp.public_view_token || 'cd-calendario-2026'}`}
                  className="text-xs bg-white"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(`${FRONTEND_URL}/calendario-cd/${configTemp.public_view_token || 'cd-calendario-2026'}`);
                    toast.success('Link copiado!');
                  }}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <div className="mt-2">
                <Label className="text-xs">Token de acceso (cambiar si querés invalidar links anteriores)</Label>
                <Input
                  value={configTemp.public_view_token || 'cd-calendario-2026'}
                  onChange={(e) => setConfigTemp({ ...configTemp, public_view_token: e.target.value })}
                  placeholder="token-secreto"
                  className="text-xs mt-1"
                />
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <Label>Notificaciones activas</Label>
              <Switch
                checked={configTemp.notificaciones_activas}
                onCheckedChange={(checked) => setConfigTemp({ ...configTemp, notificaciones_activas: checked })}
              />
            </div>
            
            <div>
              <Label>Emails de la Comisión Directiva</Label>
              <p className="text-sm text-gray-500 mb-2">Estos emails recibirán resúmenes y alertas</p>
              
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="email@ejemplo.com"
                  value={nuevoEmailCD}
                  onChange={(e) => setNuevoEmailCD(e.target.value)}
                />
                <Button type="button" variant="outline" onClick={agregarEmailCD}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {configTemp.emails_cd?.length > 0 && (
                <div className="mt-2 space-y-1">
                  {configTemp.emails_cd.map((email, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                      <span>{email}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 text-red-500"
                        onClick={() => quitarEmailCD(email)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigModal(false)}>Cancelar</Button>
            <Button onClick={handleGuardarConfig}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Toaster position="top-center" richColors />
    </div>
  );
}

// Componente EventoCard
function EventoCard({ evento, showDate = false, onEdit, onComplete, onDelete }) {
  const formatearFecha = (fecha) => {
    try {
      const [year, month, day] = fecha.split('-');
      return `${day}/${month}`;
    } catch {
      return fecha;
    }
  };

  const getPrioridadDot = (prioridad) => {
    const colors = {
      baja: 'bg-green-500',
      media: 'bg-blue-500',
      alta: 'bg-amber-500',
      urgente: 'bg-red-500'
    };
    return colors[prioridad] || 'bg-gray-500';
  };

  const getTipoIcon = (tipo) => {
    const t = TIPOS_EVENTO.find(te => te.value === tipo);
    return t?.icon || '📌';
  };

  return (
    <div className={`p-3 rounded-lg border ${evento.estado === 'completado' ? 'bg-gray-50 opacity-60' : 'bg-white'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${getPrioridadDot(evento.prioridad)}`} />
            <span className="text-lg">{getTipoIcon(evento.tipo)}</span>
            <span className={`font-medium ${evento.estado === 'completado' ? 'line-through text-gray-500' : ''}`}>
              {evento.titulo}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
            {showDate && (
              <span>
                {formatearFecha(evento.fecha_inicio)}
                {evento.fecha_fin && evento.fecha_fin !== evento.fecha_inicio && (
                  <> - {formatearFecha(evento.fecha_fin)}</>
                )}
              </span>
            )}
            {evento.hora_inicio && (
              <span>
                {evento.hora_inicio}
                {evento.hora_fin && <>-{evento.hora_fin}</>}
                hs
              </span>
            )}
            {evento.estado && evento.estado !== 'pendiente' && (
              <Badge className={ESTADOS.find(e => e.value === evento.estado)?.color}>
                {ESTADOS.find(e => e.value === evento.estado)?.label}
              </Badge>
            )}
          </div>
          {evento.descripcion && (
            <p className="text-sm text-gray-600 mt-1 line-clamp-1">{evento.descripcion}</p>
          )}
        </div>
        
        <div className="flex gap-1">
          {evento.estado !== 'completado' && evento.estado !== 'cancelado' && (
            <Button variant="ghost" size="sm" className="h-8 text-green-600" onClick={onComplete}>
              <CheckCircle className="w-4 h-4" />
            </Button>
          )}
          <Button variant="ghost" size="sm" className="h-8" onClick={onEdit}>
            <Edit className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" className="h-8 text-red-600" onClick={onDelete}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
